import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, TrendingUp, Users, LogOut, ArrowRight, ExternalLink } from 'lucide-react';
import { pb } from '../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/common/Logo';

export default function PartnerDashboard() {
    const navigate = useNavigate();
    const user = pb.authStore.model;
    const [copied, setCopied] = useState(false);
    const [stats, setStats] = useState({ clicks: 0, referrals: 0, rate: 0.5 });
    const [stripeLinkLoading, setStripeLinkLoading] = useState(false);
    const [stripeDashboardLoading, setStripeDashboardLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('DE');

    // Billing Details State
    const [billingAddress, setBillingAddress] = useState(user?.billingAddress || '');
    const [taxId, setTaxId] = useState(user?.taxId || '');
    const [isSavingBilling, setIsSavingBilling] = useState(false);
    const [billingSaved, setBillingSaved] = useState(false);

    const countries = [
        { code: 'DE', name: 'Deutschland' },
        { code: 'AT', name: 'Österreich' },
        { code: 'CH', name: 'Schweiz' },
        { code: 'LI', name: 'Liechtenstein' },
        { code: 'LU', name: 'Luxemburg' },
    ];

    useEffect(() => {
        if (!pb.authStore.isValid) {
            navigate('/partner-login');
            return;
        }

        // Load stats and billing info from User model
        setStats({
            // Mock clicks for now or fetch if we add a tracker
            clicks: 0,
            referrals: user?.totalReferrals || 0,
            rate: user?.affiliateRate || 0
        });
        setBillingAddress(user?.billingAddress || '');
        setTaxId(user?.taxId || '');

    }, [navigate, user]);

    const handleSaveBilling = async () => {
        if (!user) return;
        setIsSavingBilling(true);
        setBillingSaved(false);
        try {
            await pb.collection('users').update(user.id, {
                billingAddress,
                taxId
            });
            setBillingSaved(true);
            setTimeout(() => setBillingSaved(false), 3000);
        } catch (error) {
            console.error("Fehler beim Speichern der Rechnungsdaten:", error);
            alert("Fehler beim Speichern der Daten.");
        } finally {
            setIsSavingBilling(false);
        }
    };

    const handleLogout = () => {
        pb.authStore.clear();
        navigate('/');
    };

    const trackingLink = `https://reputrust.ai/?ref=${user?.id}`;

    const handleCopy = () => {
        const textArea = document.createElement("textarea");
        textArea.value = trackingLink;
        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';
        document.body.prepend(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error(error);
            prompt("Dein Browser blockiert das direkte Kopieren. Bitte kopiere den Link manuell:", trackingLink);
        } finally {
            textArea.remove();
        }
    };

    const handleConnectStripe = async () => {
        setStripeLinkLoading(true);
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';

            const res = await fetch(`${apiUrl}/api/create-connect-account`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: user?.email, country: selectedCountry })
            });
            const data = await res.json();
            if (data.url) {
                // In a production app, we would save the accountId directly to their PB record here, 
                // but for now, they return to /partner-success which sets it in localStorage anyway or we can update PB.
                window.location.href = data.url;
            } else {
                alert("Fehler beim Erstellen des Stripe-Kontos.");
            }
        } catch (err) {
            console.error(err);
            alert("Fehler bei der Verbindung zu Stripe.");
        } finally {
            setStripeLinkLoading(false);
        }
    };

    const handleStripeDashboard = async () => {
        setStripeDashboardLoading(true);
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';

            const res = await fetch(`${apiUrl}/api/create-express-dashboard-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stripeAccountId: user?.stripeAccountId })
            });
            const data = await res.json();
            if (data.url) {
                window.open(data.url, '_blank');
            } else {
                alert("Fehler beim Öffnen des Stripe-Dashboards.");
            }
        } catch (err) {
            console.error(err);
            alert("Fehler bei der Verbindung zu Stripe.");
        } finally {
            setStripeDashboardLoading(false);
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Logo />
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        <span className="hidden sm:inline">Abmelden</span>
                    </button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-10">
                    <h1 className="text-3xl font-extrabold text-[#0F172A] mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                        Partner Dashboard
                    </h1>
                    <p className="text-slate-600">
                        Willkommen zurück, <span className="font-medium text-slate-800">{user.email}</span>!
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                        className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-lg">
                                <Users className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-slate-700">Erfolgreiche Sales</h3>
                        </div>
                        <p className="text-4xl font-extrabold text-[#0F172A]">{stats.referrals}</p>
                    </motion.div>

                    {stats.rate > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-emerald-50 text-tron-secondary rounded-lg">
                                    <TrendingUp className="w-5 h-5" />
                                </div>
                                <h3 className="font-semibold text-slate-700">Dein Provisions-Satz</h3>
                            </div>
                            <p className="text-4xl font-extrabold text-[#0F172A]">{stats.rate * 100}%</p>
                        </motion.div>
                    )}
                </div>

                {/* Conditional Rendering: Show Link if Stripe is connected, else show Connect Prompt */}
                {user.stripeAccountId ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                        className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-200 mb-10 relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-tron-primary/10 rounded-full blur-3xl -mr-20 -mt-20"></div>

                        <h2 className="text-xl font-bold text-[#0F172A] mb-2 relative z-10" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                            Dein persönlicher Tracking-Link
                        </h2>
                        <p className="text-slate-600 mb-6 relative z-10">
                            Teile diesen Link mit Interessenten. Jeder erfolgreiche Kauf über diesen Link wird dir automatisch zugeordnet.
                        </p>

                        <div
                            onClick={handleCopy}
                            className="relative z-10 flex items-center gap-3 bg-slate-50 hover:bg-slate-100 cursor-pointer rounded-xl p-4 border border-slate-200 transition-colors group"
                        >
                            <code className="text-sm sm:text-base font-mono text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] truncate flex-1 pointer-events-none">
                                {trackingLink}
                            </code>
                            <div className="p-2 bg-white rounded-lg shadow-sm group-hover:shadow text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] transition-all pointer-events-none">
                                <Copy className="w-4 h-4" />
                            </div>
                        </div>
                        {copied && (
                            <p className="text-sm text-green-600 mt-3 font-medium animate-pulse relative z-10">
                                Link in Zwischenablage kopiert!
                            </p>
                        )}

                        <div className="mt-8 pt-8 border-t border-slate-100 relative z-10 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div>
                                <h3 className="font-bold text-slate-800 mb-1">Deine Einnahmen</h3>
                                <p className="text-sm text-slate-500">Prüfe deine Provisionen im Stripe Express Dashboard.</p>
                            </div>
                            <button
                                onClick={handleStripeDashboard}
                                disabled={stripeDashboardLoading}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-xl transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:opacity-50"
                            >
                                {stripeDashboardLoading ? 'Wird geladen...' : 'Zu Stripe wechseln'}
                                <ExternalLink className="w-4 h-4 text-slate-400" />
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                        className="bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-100 p-6 sm:p-8 rounded-2xl"
                    >
                        <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                            Auszahlungen aktivieren
                        </h2>
                        <p className="text-slate-700 mb-6 max-w-2xl">
                            Um deinen persönlichen Tracking-Link zu erhalten und Provisionen automatisch ausgezahlt zu bekommen, musst du ein kostenloses "Stripe Express" Konto einrichten.
                            Dort gibst du an, auf welches Bankkonto wir deine Provisionen überweisen sollen.
                        </p>

                        <div className="mb-6">
                            <label className="block text-sm font-bold text-slate-800 mb-2">
                                In welchem Land befindet sich dein Unternehmen/Bankkonto?
                            </label>
                            <select
                                value={selectedCountry}
                                onChange={(e) => setSelectedCountry(e.target.value)}
                                className="block w-full max-w-sm px-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                            >
                                {countries.map(c => (
                                    <option key={c.code} value={c.code}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={handleConnectStripe}
                            disabled={stripeLinkLoading}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F172A] hover:bg-slate-800 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
                        >
                            {stripeLinkLoading ? 'Wird geladen...' : 'Stripe-Konto erstellen & Link erhalten'}
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

                {/* Billing Details Form */}
                {user.stripeAccountId && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                        className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-200 mb-10"
                    >
                        <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                            Deine Rechnungsdaten (Für Gutschriften)
                        </h2>
                        <p className="text-slate-600 mb-6 max-w-2xl">
                            Damit wir dir rechtsgültige Gutschriften für deine Provisionen (Self-Billing Rechnungen) ausstellen können, benötigen wir deine vollständige Adresse und ggf. Umsatzsteuer-Identifikationsnummer.
                        </p>

                        <div className="space-y-4 max-w-xl">
                            <div>
                                <label className="block text-sm font-bold text-slate-800 mb-2">
                                    Vollständige Rechnungsadresse <span className="text-slate-400 font-normal">(Name, Straße, PLZ Ort)</span>
                                </label>
                                <textarea
                                    value={billingAddress}
                                    onChange={(e) => setBillingAddress(e.target.value)}
                                    placeholder="Max Mustermann&#10;Musterstraße 1&#10;12345 Musterstadt"
                                    rows={3}
                                    className="block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-800 mb-2">
                                    Steuernummer / USt-IdNr. <span className="text-slate-400 font-normal">(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={taxId}
                                    onChange={(e) => setTaxId(e.target.value)}
                                    placeholder="z.B. DE123456789"
                                    className="block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                                />
                            </div>

                            <button
                                onClick={handleSaveBilling}
                                disabled={isSavingBilling}
                                className="mt-4 px-6 py-3 bg-[#0F172A] hover:bg-slate-800 text-white font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {isSavingBilling ? 'Wird gespeichert...' : 'Daten speichern'}
                                {billingSaved && <span className="text-green-400">✓</span>}
                            </button>
                        </div>
                    </motion.div>
                )}
            </main>
        </div>
    );
}
